import { useEffect, useState, useMemo, useCallback } from "react";
import {
  ArrowLeft,
  Search,
  Filter,
  Plus,
  FileText,
  Clock,
  CheckCircle2,
  AlertCircle,
  Receipt,
  Download,
  Eye,
  Edit2,
  RefreshCw,
  Building2,
  Calendar,
  Layers,
  ChevronRight,
  ExternalLink,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  FileSpreadsheet,
  Truck,
  Upload,
  History,
  X,
  ShieldCheck,
  Check
} from "lucide-react";
import { api } from "../../lib/api";
import { compactMoney, money, badgeTone } from "../../lib/format";
import { toast } from "sonner";
import { isAccountsAuthorized } from "../../lib/permissions";
import Pagination, { usePagination } from "./Pagination";

export default function ClientAccounts({ clientId, user, onBack, onOpenOrder }) {
  const [data, setData] = useState({ client: null, orders: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [refreshing, setRefreshing] = useState(false);

  // Active Modals
  const [piModal, setPiModal] = useState({ isOpen: false, order: null, pi: null, isEdit: false });
  const [challanModal, setChallanModal] = useState({ isOpen: false, order: null, challan: null, isEdit: false });
  const [receiveModal, setReceiveModal] = useState({ isOpen: false, challan: null, order: null });
  const [invoiceModal, setInvoiceModal] = useState({ isOpen: false, order: null, invoice: null, isEdit: false });
  const [auditModal, setAuditModal] = useState({ isOpen: false, title: "", history: [], versions: [] });
  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    title: "",
    docId: null,
    docName: "",
    category: "",
    url: null,
    contentType: "",
    loading: false,
    error: null,
  });

  const canManage = isAccountsAuthorized(user);

  const loadClientAccounts = useCallback(async (isSilent = false) => {
    if (!clientId) return;
    if (!isSilent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await api("get", `/accounts/clients/${clientId}`);
      setData(res.data || { client: null, orders: [], summary: {} });
    } catch (err) {
      toast.error("Failed to load client accounts data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadClientAccounts();
  }, [loadClientAccounts]);

  const client = data.client;
  const summary = data.summary || {};

  // Filter orders
  const filteredOrders = useMemo(() => {
    let list = data.orders || [];
    const q = search.trim().toLowerCase();

    if (q) {
      list = list.filter((o) => {
        return (
          (o.order_id || "").toLowerCase().includes(q) ||
          (o.batch_id || "").toLowerCase().includes(q) ||
          (o.product || "").toLowerCase().includes(q) ||
          (o.product_code || "").toLowerCase().includes(q) ||
          (o.pi?.pi_number || "").toLowerCase().includes(q) ||
          (o.challans || []).some((c) => (c.challan_number || "").toLowerCase().includes(q)) ||
          (o.invoice?.invoice_number || "").toLowerCase().includes(q)
        );
      });
    }

    if (statusFilter !== "all") {
      list = list.filter((o) => o.accounts_status === statusFilter);
    }

    return list;
  }, [data.orders, search, statusFilter]);

  const {
    paginatedItems: paginatedOrders,
    currentPage,
    totalPages,
    setCurrentPage,
    pageSize,
    setPageSize,
  } = usePagination(filteredOrders, 10);

  // Document action handlers
  const handleViewDoc = async (docId, title = "Document Preview", fallbackName = "document.pdf", category = "Document") => {
    if (!docId) {
      toast.error("No document attached to this record.");
      return;
    }

    setPreviewModal({
      isOpen: true,
      title: title || "Document Preview",
      docId,
      docName: fallbackName,
      category,
      url: null,
      contentType: "",
      loading: true,
      error: null,
    });

    try {
      const res = await api("get", `/documents/${docId}/download?inline=1`, null, {
        responseType: "blob",
      });

      const contentType = res.headers["content-type"] || "application/pdf";
      const blob = new Blob([res.data], { type: contentType });
      const blobUrl = URL.createObjectURL(blob);

      setPreviewModal((prev) => ({
        ...prev,
        url: blobUrl,
        contentType,
        loading: false,
        error: null,
      }));
    } catch (err) {
      let errMsg = "Document file is unavailable in storage or could not be loaded.";
      if (err.response?.status === 404) {
        errMsg = "Document file was not found in storage. You can edit or replace it using the Replace action.";
      } else if (err.response?.status === 403) {
        errMsg = "You do not have permission to view this document.";
      } else if (err.response?.status === 401) {
        errMsg = "Your session has expired. Please sign in again.";
      }
      setPreviewModal((prev) => ({
        ...prev,
        loading: false,
        error: errMsg,
      }));
      toast.error(errMsg);
    }
  };

  const handleDownloadDoc = async (docId, fallbackName = "document.pdf") => {
    if (!docId) {
      toast.error("No document attached to download.");
      return;
    }
    const toastId = toast.loading("Preparing download...");
    try {
      const res = await api("get", `/documents/${docId}/download?inline=0`, null, {
        responseType: "blob",
      });
      let filename = fallbackName;
      const disposition = res.headers["content-disposition"];
      if (disposition && disposition.includes("filename=")) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) filename = match[1];
      }
      const contentType = res.headers["content-type"] || "application/octet-stream";
      const blob = new Blob([res.data], { type: contentType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.dismiss(toastId);
      toast.success("Download started");
    } catch (err) {
      toast.dismiss(toastId);
      if (err.response?.status === 404) {
        toast.error("Document file was not found in storage.");
      } else if (err.response?.status === 403) {
        toast.error("You do not have permission to download this document.");
      } else {
        toast.error("Failed to download document.");
      }
    }
  };

  const closePreviewModal = () => {
    if (previewModal.url) {
      try {
        URL.revokeObjectURL(previewModal.url);
      } catch {}
    }
    setPreviewModal({
      isOpen: false,
      title: "",
      docId: null,
      docName: "",
      category: "",
      url: null,
      contentType: "",
      loading: false,
      error: null,
    });
  };

  if (loading && !client) {
    return (
      <div className="content" style={{ padding: 40, textAlign: "center", color: "#64748b" }}>
        Loading Client Accounts Workspace…
      </div>
    );
  }

  if (!client) {
    return (
      <div className="content" style={{ padding: 40, textAlign: "center" }}>
        <p style={{ color: "#ef4444", fontSize: 16 }}>Client not found</p>
        <button className="outline-btn compact" onClick={onBack}>
          <ArrowLeft size={14} /> Back to Accounts
        </button>
      </div>
    );
  }

  return (
    <div className="content detail-content" data-testid="client-accounts-page" style={{ maxWidth: 1400, margin: "0 auto", paddingBottom: 60 }}>
      {/* Back button & top bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <button
          className="back-btn"
          onClick={onBack}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600, fontSize: 13 }}
          data-testid="client-accounts-back-btn"
        >
          <ArrowLeft size={14} /> Back to Accounts
        </button>

        <button
          className="outline-btn compact"
          onClick={() => loadClientAccounts(true)}
          disabled={refreshing}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12 }}
          data-testid="client-accounts-refresh-btn"
        >
          <RefreshCw size={13} className={refreshing ? "spin" : ""} />
          {refreshing ? "Refreshing…" : "Refresh Workspace"}
        </button>
      </div>

      {/* Client Overview Card */}
      <div
        className="panel"
        style={{
          background: "#ffffff",
          border: "1px solid var(--line)",
          borderRadius: 8,
          padding: "18px 22px",
          marginBottom: 18,
          boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16 }}>
          {/* Client Details */}
          <div style={{ flex: 1, minWidth: 280 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  borderRadius: 6,
                  background: "#e0f2fe",
                  color: "#0369a1",
                }}
              >
                <Building2 size={18} />
              </span>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "var(--navy)" }}>
                {client.company_name}
              </h2>
              <span className="mono" style={{ fontSize: 11.5, background: "#f1f5f9", padding: "2px 7px", borderRadius: 4, color: "#475569" }}>
                {client.client_id}
              </span>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 18px", fontSize: 12.5, color: "#64748b", marginTop: 10 }}>
              {client.contact_person && <span>👤 Contact: <b>{client.contact_person}</b></span>}
              {client.email && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <Mail size={12} /> {client.email}
                </span>
              )}
              {client.phone && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <Phone size={12} /> {client.phone}
                </span>
              )}
              {client.gstin && <span className="mono">GSTIN: <b>{client.gstin}</b></span>}
              {client.pan && <span className="mono">PAN: <b>{client.pan}</b></span>}
              <span>Terms: <b>{client.payment_terms || "Net 30"}</b></span>
            </div>
          </div>

          {/* Quick Metrics Banner */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 10,
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 6,
              padding: "10px 14px",
              minWidth: 320,
            }}
          >
            <div>
              <span style={{ fontSize: 10.5, fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}>Orders</span>
              <div style={{ fontSize: 16, fontWeight: 700, color: "var(--navy)" }}>{summary.total_orders || 0}</div>
            </div>
            <div>
              <span style={{ fontSize: 10.5, fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}>Total Value</span>
              <div style={{ fontSize: 16, fontWeight: 700, color: "var(--navy)" }}>{compactMoney(summary.total_value || 0)}</div>
            </div>
            <div>
              <span style={{ fontSize: 10.5, fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}>Invoiced</span>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#16a34a" }}>{summary.invoice_count || 0}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div
        className="panel"
        style={{
          background: "#ffffff",
          border: "1px solid var(--line)",
          borderRadius: 8,
          padding: "12px 16px",
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 260 }}>
          <div style={{ position: "relative", width: "100%", maxWidth: 360 }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
            <input
              type="text"
              placeholder="Search by Order ID, Batch ID, Product, Doc #..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%",
                paddingLeft: 32,
                paddingRight: 10,
                height: 34,
                fontSize: 13,
                borderRadius: 6,
                border: "1px solid var(--line)",
                background: "#f8fafc",
              }}
              data-testid="client-accounts-search"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              height: 34,
              fontSize: 12.5,
              padding: "0 10px",
              borderRadius: 6,
              border: "1px solid var(--line)",
              background: "#ffffff",
              color: "#334155",
            }}
            data-testid="status-filter-select"
          >
            <option value="all">All Stages</option>
            <option value="Pending PI">Pending PI</option>
            <option value="PI Issued">PI Issued</option>
            <option value="Challan Issued">Challan Issued (Awaiting Receipt)</option>
            <option value="Challan Received">Challan Received</option>
            <option value="Completed">Completed / Invoiced</option>
          </select>

          {statusFilter !== "all" && (
            <button
              className="outline-btn compact"
              onClick={() => setStatusFilter("all")}
              style={{ fontSize: 11.5, height: 32, padding: "0 10px", color: "#64748b" }}
            >
              Reset Filter
            </button>
          )}
        </div>

        <div style={{ fontSize: 12.5, color: "#64748b" }}>
          Showing <b>{filteredOrders.length}</b> orders
        </div>
      </div>

      {/* Orders & Batches Financial Lifecycle Cards / Table */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {paginatedOrders.length === 0 ? (
          <div
            className="panel"
            style={{
              background: "#ffffff",
              border: "1px solid var(--line)",
              borderRadius: 8,
              padding: 40,
              textAlign: "center",
              color: "#94a3b8",
            }}
          >
            No orders found for this client matching the criteria.
          </div>
        ) : (
          paginatedOrders.map((ord) => {
            const pi = ord.pi;
            const challans = ord.challans || [];
            const invoice = ord.invoice;

            return (
              <div
                key={ord.order_id}
                className="panel"
                style={{
                  background: "#ffffff",
                  border: "1px solid var(--line)",
                  borderRadius: 8,
                  padding: "16px 18px",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
                }}
                data-testid={`order-card-${ord.order_id}`}
              >
                {/* Order Header Line */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10, marginBottom: 14, borderBottom: "1px solid #f1f5f9", paddingBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <button
                      className="link-btn"
                      onClick={() => onOpenOrder && onOpenOrder(ord.order_id)}
                      style={{ fontWeight: 700, fontSize: 14.5, color: "#0284c7", display: "inline-flex", alignItems: "center", gap: 4 }}
                      title="Open Order Details"
                    >
                      #{ord.order_id} <ExternalLink size={12} />
                    </button>

                    {ord.batch_id && (
                      <span
                        className="mono"
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#7e22ce",
                          background: "#f3e8ff",
                          border: "1px solid #e9d5ff",
                          padding: "2px 7px",
                          borderRadius: 4,
                        }}
                      >
                        Batch #{ord.batch_id}
                      </span>
                    )}

                    <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--navy)" }}>
                      {ord.product}
                    </span>

                    <span style={{ fontSize: 12, color: "#64748b" }}>
                      Qty: <b>{ord.quantity}</b> {ord.unit}
                    </span>

                    <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--navy)" }}>
                      Total: {money(ord.total_value || 0)}
                    </span>
                  </div>

                  {/* Operational Stage & Accounts Overall Status */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span className="badge gray" style={{ fontSize: 11, padding: "2px 8px" }} title="Current Operational Stage">
                      Op: {ord.current_stage}
                    </span>

                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: 11.5,
                        fontWeight: 700,
                        padding: "3px 9px",
                        borderRadius: 4,
                        color:
                          ord.accounts_status === "Completed"
                            ? "#15803d"
                            : ord.accounts_status === "Challan Received"
                            ? "#0369a1"
                            : ord.accounts_status === "Challan Issued"
                            ? "#7e22ce"
                            : ord.accounts_status === "PI Issued"
                            ? "#b45309"
                            : "#64748b",
                        background:
                          ord.accounts_status === "Completed"
                            ? "#dcfce7"
                            : ord.accounts_status === "Challan Received"
                            ? "#e0f2fe"
                            : ord.accounts_status === "Challan Issued"
                            ? "#f3e8ff"
                            : ord.accounts_status === "PI Issued"
                            ? "#fef3c7"
                            : "#f1f5f9",
                      }}
                      data-testid={`accounts-status-${ord.order_id}`}
                    >
                      {ord.accounts_status}
                    </span>
                  </div>
                </div>

                {/* 4-Stage Document Lifecycle Grid */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: 12,
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: 6,
                    padding: "12px 14px",
                  }}
                >
                  {/* STAGE A: PROFORMA INVOICE (PI) */}
                  <div
                    style={{
                      background: "#ffffff",
                      border: "1px solid #e2e8f0",
                      borderRadius: 6,
                      padding: "10px 12px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                    data-testid={`stage-pi-${ord.order_id}`}
                  >
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#b45309", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                          Stage A: Proforma Invoice
                        </span>
                        <span className={`badge ${pi ? "green" : "gray"}`} style={{ fontSize: 10, padding: "1px 6px" }}>
                          {pi ? (pi.status || "Issued") : "Not Created"}
                        </span>
                      </div>

                      {pi ? (
                        <div style={{ fontSize: 12, color: "#334155" }}>
                          <div style={{ fontWeight: 600, color: "var(--navy)", marginBottom: 2 }}>
                            #{pi.pi_number}
                          </div>
                          <div style={{ color: "#64748b", fontSize: 11 }}>Date: {pi.issue_date}</div>
                          <div style={{ color: "var(--navy)", fontWeight: 600, marginTop: 2 }}>
                            Amount: {money(pi.total_amount || 0)}
                          </div>
                          {pi.due_date && <div style={{ fontSize: 10.5, color: "#94a3b8" }}>Due: {pi.due_date}</div>}
                        </div>
                      ) : (
                        <p style={{ margin: "4px 0", fontSize: 11.5, color: "#94a3b8", fontStyle: "italic" }}>
                          No Proforma Invoice issued for this order yet.
                        </p>
                      )}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10, flexWrap: "wrap", borderTop: "1px solid #f1f5f9", paddingTop: 8 }}>
                      {pi ? (
                        <>
                          <button
                            className="outline-btn compact"
                            style={{ fontSize: 11, height: 26, padding: "0 7px" }}
                            onClick={() => {
                              const docId = pi.document_id || ord.pi_document_id || pi.pi_id;
                              handleViewDoc(
                                docId,
                                `Proforma Invoice #${pi.pi_number}`,
                                pi.document_name || `${pi.pi_number}.pdf`,
                                "Proforma Invoice"
                              );
                            }}
                            title="Preview PI Document"
                            data-testid={`view-pi-btn-${ord.order_id}`}
                          >
                            <Eye size={11} /> View
                          </button>
                          <button
                            className="outline-btn compact"
                            style={{ fontSize: 11, height: 26, padding: "0 7px" }}
                            onClick={() => {
                              const docId = pi.document_id || ord.pi_document_id || pi.pi_id;
                              handleDownloadDoc(docId, pi.document_name || `${pi.pi_number}.pdf`);
                            }}
                            title="Download PI File"
                            data-testid={`download-pi-btn-${ord.order_id}`}
                          >
                            <Download size={11} />
                          </button>
                          {canManage && (
                            <button
                              className="outline-btn compact"
                              style={{ fontSize: 11, height: 26, padding: "0 7px", color: "#0284c7" }}
                              onClick={() => setPiModal({ isOpen: true, order: ord, pi, isEdit: true })}
                              title="Edit / Replace PI"
                            >
                              <Edit2 size={11} /> Replace
                            </button>
                          )}
                          <button
                            className="outline-btn compact"
                            style={{ fontSize: 11, height: 26, padding: "0 6px", color: "#64748b" }}
                            onClick={() => setAuditModal({ isOpen: true, title: `PI #${pi.pi_number} Audit Trail`, history: pi.history || [], versions: pi.versions || [] })}
                            title="View History"
                          >
                            <History size={11} />
                          </button>
                        </>
                      ) : (
                        canManage && (
                          <button
                            className="outline-btn compact"
                            style={{ fontSize: 11, height: 26, padding: "0 8px", color: "#b45309", borderColor: "#fde68a", background: "#fffbeb" }}
                            onClick={() => setPiModal({ isOpen: true, order: ord, pi: null, isEdit: false })}
                            data-testid={`create-pi-btn-${ord.order_id}`}
                          >
                            <Plus size={11} /> Create / Upload PI
                          </button>
                        )
                      )}
                    </div>
                  </div>

                  {/* STAGE B & C: CHALLAN RECORDS */}
                  <div
                    style={{
                      background: "#ffffff",
                      border: "1px solid #e2e8f0",
                      borderRadius: 6,
                      padding: "10px 12px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                    data-testid={`stage-challan-${ord.order_id}`}
                  >
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#7e22ce", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                          Stage B &amp; C: Challan Records
                        </span>
                        <span
                          className={`badge ${
                            ord.challan_status === "Received"
                              ? "green"
                              : ord.challan_status === "Issued"
                              ? "amber"
                              : "gray"
                          }`}
                          style={{ fontSize: 10, padding: "1px 6px" }}
                        >
                          {ord.challan_status}
                        </span>
                      </div>

                      {challans.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          {challans.map((ch, cIdx) => {
                            const challanDocId = ch.pdf_document_id || ch.document_id || ord.challan_document_id || ch.challan_id;
                            const proofDocId = ch.receipt_document_id || ch.proof_document_id;

                            return (
                              <div key={ch.challan_id || cIdx} style={{ fontSize: 11.5, borderBottom: cIdx < challans.length - 1 ? "1px dashed #e2e8f0" : "none", paddingBottom: 4 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <span style={{ fontWeight: 600, color: "var(--navy)" }}>#{ch.challan_number}</span>
                                  <span style={{ fontSize: 10, color: ch.is_received ? "#15803d" : "#b45309", fontWeight: 600 }}>
                                    {ch.is_received ? "✓ Received" : "⏳ Awaiting Receipt"}
                                  </span>
                                </div>
                                <div style={{ fontSize: 10.5, color: "#64748b" }}>Date: {ch.challan_date} · Qty: {ch.quantity}</div>
                                {ch.is_received && ch.received_date && (
                                  <div style={{ fontSize: 10.5, color: "#166534" }}>Received on {ch.received_date} by {ch.received_by}</div>
                                )}

                                <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap", alignItems: "center" }}>
                                  <button
                                    className="outline-btn compact"
                                    style={{ fontSize: 10, height: 22, padding: "0 6px" }}
                                    onClick={() => handleViewDoc(
                                      challanDocId,
                                      `Delivery Challan #${ch.challan_number}`,
                                      ch.document_name || `${ch.challan_number}.pdf`,
                                      "Delivery Challan"
                                    )}
                                    title="View Challan Document"
                                    data-testid={`view-challan-btn-${ch.challan_id || ord.order_id}`}
                                  >
                                    <Eye size={10} /> View
                                  </button>

                                  {challanDocId && (
                                    <button
                                      className="outline-btn compact"
                                      style={{ fontSize: 10, height: 22, padding: "0 5px", color: "#64748b" }}
                                      onClick={() => handleDownloadDoc(challanDocId, ch.document_name || `${ch.challan_number}.pdf`)}
                                      title="Download Challan File"
                                      data-testid={`download-challan-btn-${ch.challan_id || ord.order_id}`}
                                    >
                                      <Download size={10} />
                                    </button>
                                  )}

                                  {proofDocId && (
                                    <button
                                      className="outline-btn compact"
                                      style={{ fontSize: 10, height: 22, padding: "0 6px", color: "#166534", borderColor: "#bbf7d0", background: "#f0fdf4" }}
                                      onClick={() => handleViewDoc(
                                        proofDocId,
                                        `Signed Receipt Proof — Challan #${ch.challan_number}`,
                                        `Proof-${ch.challan_number}.pdf`,
                                        "Signed Receipt Proof"
                                      )}
                                      title="View Signed Receipt Proof"
                                      data-testid={`view-proof-btn-${ch.challan_id || ord.order_id}`}
                                    >
                                      <Eye size={10} /> Proof
                                    </button>
                                  )}

                                  {!ch.is_received && canManage && (
                                    <button
                                      className="outline-btn compact"
                                      style={{ fontSize: 10, height: 22, padding: "0 6px", color: "#15803d", borderColor: "#bbf7d0", background: "#f0fdf4" }}
                                      onClick={() => setReceiveModal({ isOpen: true, challan: ch, order: ord })}
                                      data-testid={`mark-received-btn-${ch.challan_id || ord.order_id}`}
                                    >
                                      <Check size={10} /> Mark Received
                                    </button>
                                  )}
                                  {canManage && (
                                    <button
                                      className="outline-btn compact"
                                      style={{ fontSize: 10, height: 22, padding: "0 5px", color: "#0284c7" }}
                                      onClick={() => setChallanModal({ isOpen: true, order: ord, challan: ch, isEdit: true })}
                                      title="Edit Challan"
                                    >
                                      <Edit2 size={10} />
                                    </button>
                                  )}
                                  <button
                                    className="outline-btn compact"
                                    style={{ fontSize: 10, height: 22, padding: "0 5px", color: "#64748b" }}
                                    onClick={() => setAuditModal({ isOpen: true, title: `Challan #${ch.challan_number} Audit Trail`, history: ch.history || [], versions: ch.versions || [] })}
                                    title="Audit Trail"
                                  >
                                    <History size={10} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : ord.challan_number ? (
                        <div style={{ fontSize: 12, color: "#334155" }}>
                          <div>
                            <span style={{ fontWeight: 600 }}>#{ord.challan_number}</span> (Dispatched Challan)
                          </div>
                          <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                            <button
                              className="outline-btn compact"
                              style={{ fontSize: 10, height: 22, padding: "0 6px" }}
                              onClick={() => handleViewDoc(
                                ord.challan_document_id || ord.challan_number,
                                `Delivery Challan #${ord.challan_number}`,
                                `${ord.challan_number}.pdf`,
                                "Delivery Challan"
                              )}
                              title="View Dispatched Challan"
                            >
                              <Eye size={10} /> View
                            </button>
                            {ord.challan_document_id && (
                              <button
                                className="outline-btn compact"
                                style={{ fontSize: 10, height: 22, padding: "0 5px", color: "#64748b" }}
                                onClick={() => handleDownloadDoc(ord.challan_document_id, `${ord.challan_number}.pdf`)}
                                title="Download Dispatched Challan"
                              >
                                <Download size={10} />
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <p style={{ margin: "4px 0", fontSize: 11.5, color: "#94a3b8", fontStyle: "italic" }}>
                          No challan recorded yet.
                        </p>
                      )}
                    </div>

                    <div style={{ marginTop: 10, borderTop: "1px solid #f1f5f9", paddingTop: 8 }}>
                      {canManage && (
                        <button
                          className="outline-btn compact"
                          style={{ fontSize: 11, height: 26, padding: "0 8px", color: "#7e22ce", borderColor: "#e9d5ff", background: "#fdf4ff" }}
                          onClick={() => setChallanModal({ isOpen: true, order: ord, challan: null, isEdit: false })}
                          data-testid={`add-challan-btn-${ord.order_id}`}
                        >
                          <Plus size={11} /> Record / Upload Challan
                        </button>
                      )}
                    </div>
                  </div>

                  {/* STAGE D: FINAL INVOICE */}
                  <div
                    style={{
                      background: "#ffffff",
                      border: "1px solid #e2e8f0",
                      borderRadius: 6,
                      padding: "10px 12px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                    data-testid={`stage-invoice-${ord.order_id}`}
                  >
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#0369a1", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                          Stage D: Final Invoice
                        </span>
                        <span className={`badge ${invoice ? "green" : "gray"}`} style={{ fontSize: 10, padding: "1px 6px" }}>
                          {invoice ? (invoice.status || "Issued") : "Not Invoiced"}
                        </span>
                      </div>

                      {invoice ? (
                        <div style={{ fontSize: 12, color: "#334155" }}>
                          <div style={{ fontWeight: 600, color: "var(--navy)", marginBottom: 2 }}>
                            #{invoice.invoice_number}
                          </div>
                          <div style={{ color: "#64748b", fontSize: 11 }}>Date: {invoice.invoice_date}</div>
                          <div style={{ color: "var(--navy)", fontWeight: 600, marginTop: 2 }}>
                            Total: {money(invoice.total || invoice.total_amount || 0)}
                          </div>
                          <div style={{ fontSize: 10.5, color: "#64748b" }}>Status: <b>{invoice.status}</b> (Payment Tracked Separately)</div>
                        </div>
                      ) : (
                        <p style={{ margin: "4px 0", fontSize: 11.5, color: "#94a3b8", fontStyle: "italic" }}>
                          Final Invoice pending generation.
                        </p>
                      )}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10, flexWrap: "wrap", borderTop: "1px solid #f1f5f9", paddingTop: 8 }}>
                      {invoice ? (
                        <>
                          <button
                            className="outline-btn compact"
                            style={{ fontSize: 11, height: 26, padding: "0 7px" }}
                            onClick={() => {
                              const docId = invoice.pdf_document_id || invoice.document_id || ord.invoice_document_id || invoice.invoice_id;
                              handleViewDoc(
                                docId,
                                `Final Invoice #${invoice.invoice_number}`,
                                invoice.document_name || `${invoice.invoice_number}.pdf`,
                                "Final Invoice"
                              );
                            }}
                            title="Preview Invoice"
                            data-testid={`view-invoice-btn-${ord.order_id}`}
                          >
                            <Eye size={11} /> View
                          </button>
                          <button
                            className="outline-btn compact"
                            style={{ fontSize: 11, height: 26, padding: "0 7px" }}
                            onClick={() => {
                              const docId = invoice.pdf_document_id || invoice.document_id || ord.invoice_document_id || invoice.invoice_id;
                              handleDownloadDoc(docId, invoice.document_name || `${invoice.invoice_number}.pdf`);
                            }}
                            title="Download Invoice"
                            data-testid={`download-invoice-btn-${ord.order_id}`}
                          >
                            <Download size={11} />
                          </button>
                          {canManage && (
                            <button
                              className="outline-btn compact"
                              style={{ fontSize: 11, height: 26, padding: "0 7px", color: "#0284c7" }}
                              onClick={() => setInvoiceModal({ isOpen: true, order: ord, invoice, isEdit: true })}
                              title="Edit / Replace Invoice"
                            >
                              <Edit2 size={11} /> Replace
                            </button>
                          )}
                          <button
                            className="outline-btn compact"
                            style={{ fontSize: 11, height: 26, padding: "0 6px", color: "#64748b" }}
                            onClick={() => setAuditModal({ isOpen: true, title: `Invoice #${invoice.invoice_number} Audit Trail`, history: invoice.history || [], versions: invoice.versions || [] })}
                            title="View History"
                          >
                            <History size={11} />
                          </button>
                        </>
                      ) : (
                        canManage && (
                          <button
                            className="outline-btn compact"
                            style={{ fontSize: 11, height: 26, padding: "0 8px", color: "#0369a1", borderColor: "#bae6fd", background: "#f0f9ff" }}
                            onClick={() => setInvoiceModal({ isOpen: true, order: ord, invoice, isEdit: false })}
                            data-testid={`generate-invoice-btn-${ord.order_id}`}
                          >
                            <Plus size={11} /> Generate Final Invoice
                          </button>
                        )
                      )}
                    </div>
                  </div>
                </div>

                {/* Progress Step Footnote */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, fontSize: 11.5, color: "#64748b" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontWeight: 600, color: "#475569" }}>Next Step:</span>
                    <span style={{ color: ord.accounts_status === "Completed" ? "#15803d" : "#0284c7" }}>
                      {ord.next_pending_action}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontWeight: 600 }}>{ord.progress_pct}% Completed</span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ marginTop: 20 }}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            totalItems={filteredOrders.length}
          />
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 1: Proforma Invoice Create / Edit Modal */}
      {/* ========================================================================= */}
      {piModal.isOpen && (
        <PiFormModal
          order={piModal.order}
          pi={piModal.pi}
          client={client}
          isEdit={piModal.isEdit}
          onClose={() => setPiModal({ isOpen: false, order: null, pi: null, isEdit: false })}
          onSuccess={() => {
            setPiModal({ isOpen: false, order: null, pi: null, isEdit: false });
            loadClientAccounts(true);
          }}
        />
      )}

      {/* ========================================================================= */}
      {/* MODAL 2: Challan Record / Edit Modal */}
      {/* ========================================================================= */}
      {challanModal.isOpen && (
        <ChallanFormModal
          order={challanModal.order}
          challan={challanModal.challan}
          client={client}
          isEdit={challanModal.isEdit}
          onClose={() => setChallanModal({ isOpen: false, order: null, challan: null, isEdit: false })}
          onSuccess={() => {
            setChallanModal({ isOpen: false, order: null, challan: null, isEdit: false });
            loadClientAccounts(true);
          }}
        />
      )}

      {/* ========================================================================= */}
      {/* MODAL 3: Mark Challan Received Modal */}
      {/* ========================================================================= */}
      {receiveModal.isOpen && (
        <MarkReceiveModal
          challan={receiveModal.challan}
          order={receiveModal.order}
          onClose={() => setReceiveModal({ isOpen: false, challan: null, order: null })}
          onSuccess={() => {
            setReceiveModal({ isOpen: false, challan: null, order: null });
            loadClientAccounts(true);
          }}
        />
      )}

      {/* ========================================================================= */}
      {/* MODAL 4: Final Invoice Create / Edit Modal */}
      {/* ========================================================================= */}
      {invoiceModal.isOpen && (
        <InvoiceFormModal
          order={invoiceModal.order}
          invoice={invoiceModal.invoice}
          client={client}
          isEdit={invoiceModal.isEdit}
          onClose={() => setInvoiceModal({ isOpen: false, order: null, invoice: null, isEdit: false })}
          onSuccess={() => {
            setInvoiceModal({ isOpen: false, order: null, invoice: null, isEdit: false });
            loadClientAccounts(true);
          }}
        />
      )}

      {/* ========================================================================= */}
      {/* MODAL 5: Audit Trail & Version History Modal */}
      {/* ========================================================================= */}
      {auditModal.isOpen && (
        <AuditTrailModal
          title={auditModal.title}
          history={auditModal.history}
          versions={auditModal.versions}
          onClose={() => setAuditModal({ isOpen: false, title: "", history: [], versions: [] })}
          onDownloadDoc={handleDownloadDoc}
        />
      )}

      {/* ========================================================================= */}
      {/* MODAL 6: Document Preview Modal */}
      {/* ========================================================================= */}
      <DocumentPreviewModal
        preview={previewModal}
        onClose={closePreviewModal}
        onDownload={handleDownloadDoc}
      />
    </div>
  );
}

// -------------------------------------------------------------------------
// Subcomponent: Proforma Invoice Form Modal
// -------------------------------------------------------------------------
function PiFormModal({ order, pi, client, isEdit, onClose, onSuccess }) {
  const [form, setForm] = useState({
    pi_number: pi?.pi_number || "",
    issue_date: pi?.issue_date || new Date().toISOString().slice(0, 10),
    due_date: pi?.due_date || "",
    taxable_amount: pi?.taxable_amount !== undefined ? pi.taxable_amount : order?.order_value || 0,
    tax_rate: pi?.tax_rate || 18.0,
    tax_amount: pi?.tax_amount !== undefined ? pi.tax_amount : order?.gst || 0,
    total_amount: pi?.total_amount !== undefined ? pi.total_amount : order?.total_value || 0,
    remarks: pi?.remarks || "",
    reason: "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const calculateTotals = (taxable, rate) => {
    const t = parseFloat(taxable) || 0;
    const r = parseFloat(rate) || 0;
    const tax = Math.round(t * (r / 100.0) * 100) / 100;
    const tot = Math.round((t + tax) * 100) / 100;
    setForm((f) => ({ ...f, taxable_amount: t, tax_rate: r, tax_amount: tax, total_amount: tot }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      if (!isEdit) {
        if (order.batch_id) fd.append("batch_id", order.batch_id);
        else fd.append("order_id", order.order_id);
        fd.append("client_id", client.client_id);
      }
      if (form.pi_number.trim()) fd.append("pi_number", form.pi_number.trim());
      fd.append("issue_date", form.issue_date);
      if (form.due_date) fd.append("due_date", form.due_date);
      fd.append("taxable_amount", form.taxable_amount);
      fd.append("tax_rate", form.tax_rate);
      fd.append("tax_amount", form.tax_amount);
      fd.append("total_amount", form.total_amount);
      if (form.remarks) fd.append("remarks", form.remarks);
      if (isEdit && form.reason) fd.append("reason", form.reason);
      if (file) fd.append("file", file);

      if (isEdit) {
        await api("put", `/accounts/pi/${pi.pi_id}`, fd);
        toast.success("Proforma Invoice updated successfully");
      } else {
        await api("post", `/accounts/pi`, fd);
        toast.success("Proforma Invoice created successfully");
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save Proforma Invoice");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div className="panel" style={{ background: "#ffffff", borderRadius: 8, width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto", padding: 22, boxShadow: "0 10px 25px rgba(0,0,0,0.1)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, borderBottom: "1px solid var(--line)", paddingBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "var(--navy)" }}>
            {isEdit ? "Edit / Replace Proforma Invoice" : "Create Proforma Invoice (PI)"}
          </h3>
          <button className="icon-btn" onClick={onClose}><X size={16} /></button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>
              PI Number (Leave empty to auto-generate)
            </label>
            <input
              type="text"
              placeholder="e.g. PI-2610-001"
              value={form.pi_number}
              onChange={(e) => setForm({ ...form, pi_number: e.target.value })}
              style={{ width: "100%", height: 34, padding: "0 10px", fontSize: 13, border: "1px solid var(--line)", borderRadius: 6 }}
              data-testid="pi-number-input"
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Issue Date *</label>
              <input
                type="date"
                required
                value={form.issue_date}
                onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
                style={{ width: "100%", height: 34, padding: "0 8px", fontSize: 13, border: "1px solid var(--line)", borderRadius: 6 }}
                data-testid="pi-issue-date-input"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Due Date</label>
              <input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                style={{ width: "100%", height: 34, padding: "0 8px", fontSize: 13, border: "1px solid var(--line)", borderRadius: 6 }}
              />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Taxable Amount (₹) *</label>
              <input
                type="number"
                step="0.01"
                required
                value={form.taxable_amount}
                onChange={(e) => calculateTotals(e.target.value, form.tax_rate)}
                style={{ width: "100%", height: 34, padding: "0 10px", fontSize: 13, border: "1px solid var(--line)", borderRadius: 6 }}
                data-testid="pi-taxable-amount-input"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>GST Rate (%)</label>
              <input
                type="number"
                step="0.1"
                value={form.tax_rate}
                onChange={(e) => calculateTotals(form.taxable_amount, e.target.value)}
                style={{ width: "100%", height: 34, padding: "0 10px", fontSize: 13, border: "1px solid var(--line)", borderRadius: 6 }}
              />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>GST Tax Amount (₹)</label>
              <input
                type="number"
                readOnly
                value={form.tax_amount}
                style={{ width: "100%", height: 34, padding: "0 10px", fontSize: 13, border: "1px solid #e2e8f0", background: "#f8fafc", borderRadius: 6 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Total Amount (₹) *</label>
              <input
                type="number"
                step="0.01"
                required
                value={form.total_amount}
                onChange={(e) => setForm({ ...form, total_amount: parseFloat(e.target.value) || 0 })}
                style={{ width: "100%", height: 34, padding: "0 10px", fontSize: 13, fontWeight: 700, border: "1px solid var(--line)", borderRadius: 6 }}
                data-testid="pi-total-amount-input"
              />
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>
              Document Attachment (PDF or Scan)
            </label>
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
              onChange={(e) => setFile(e.target.files[0])}
              style={{ fontSize: 12, width: "100%" }}
            />
            <small style={{ color: "#94a3b8", display: "block", marginTop: 2 }}>
              If no file is selected, the CRM will automatically generate a PDF Proforma Invoice.
            </small>
          </div>

          {isEdit && (
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>
                Reason for change / replacement *
              </label>
              <input
                type="text"
                required
                placeholder="e.g. Updated line items or corrected tax rate"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                style={{ width: "100%", height: 34, padding: "0 10px", fontSize: 13, border: "1px solid var(--line)", borderRadius: 6 }}
              />
            </div>
          )}

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Remarks</label>
            <textarea
              rows={2}
              value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })}
              style={{ width: "100%", padding: 8, fontSize: 12.5, border: "1px solid var(--line)", borderRadius: 6 }}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12 }}>
            <button type="button" className="outline-btn compact" onClick={onClose}>Cancel</button>
            <button
              type="submit"
              className="primary-btn compact"
              disabled={busy}
              style={{ minWidth: 120 }}
              data-testid="submit-pi-btn"
            >
              {busy ? "Saving…" : isEdit ? "Update PI" : "Create PI"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Subcomponent: Challan Record / Edit Modal
// -------------------------------------------------------------------------
function ChallanFormModal({ order, challan, client, isEdit, onClose, onSuccess }) {
  const [form, setForm] = useState({
    challan_number: challan?.challan_number || "",
    challan_date: challan?.challan_date || new Date().toISOString().slice(0, 10),
    quantity: challan?.quantity !== undefined ? challan.quantity : order?.quantity || 0,
    items_covered: challan?.items_covered || order?.product || "",
    transporter: challan?.transporter || order?.assigned_delivery_person || "In-House Logistics",
    tracking_number: challan?.tracking_number || "",
    remarks: challan?.remarks || "",
    reason: "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      if (!isEdit) {
        if (order.batch_id) fd.append("batch_id", order.batch_id);
        else fd.append("order_id", order.order_id);
        fd.append("client_id", client.client_id);
      }
      if (form.challan_number.trim()) fd.append("challan_number", form.challan_number.trim());
      fd.append("challan_date", form.challan_date);
      fd.append("quantity", form.quantity);
      if (form.items_covered) fd.append("items_covered", form.items_covered);
      if (form.transporter) fd.append("transporter", form.transporter);
      if (form.tracking_number) fd.append("tracking_number", form.tracking_number);
      if (form.remarks) fd.append("remarks", form.remarks);
      if (isEdit && form.reason) fd.append("reason", form.reason);
      if (file) fd.append("file", file);

      if (isEdit) {
        await api("put", `/accounts/challans/${challan.challan_id}`, fd);
        toast.success("Challan updated successfully");
      } else {
        await api("post", `/accounts/challans`, fd);
        toast.success("Challan recorded successfully");
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record Challan");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div className="panel" style={{ background: "#ffffff", borderRadius: 8, width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto", padding: 22, boxShadow: "0 10px 25px rgba(0,0,0,0.1)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, borderBottom: "1px solid var(--line)", paddingBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "var(--navy)" }}>
            {isEdit ? "Edit / Replace Challan" : "Record Delivery Challan"}
          </h3>
          <button className="icon-btn" onClick={onClose}><X size={16} /></button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>
              Challan Number (Leave empty to auto-generate)
            </label>
            <input
              type="text"
              placeholder="e.g. CH-2610-001"
              value={form.challan_number}
              onChange={(e) => setForm({ ...form, challan_number: e.target.value })}
              style={{ width: "100%", height: 34, padding: "0 10px", fontSize: 13, border: "1px solid var(--line)", borderRadius: 6 }}
              data-testid="challan-number-input"
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Issue Date *</label>
              <input
                type="date"
                required
                value={form.challan_date}
                onChange={(e) => setForm({ ...form, challan_date: e.target.value })}
                style={{ width: "100%", height: 34, padding: "0 8px", fontSize: 13, border: "1px solid var(--line)", borderRadius: 6 }}
                data-testid="challan-date-input"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Quantity Covered *</label>
              <input
                type="number"
                required
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value) || 0 })}
                style={{ width: "100%", height: 34, padding: "0 10px", fontSize: 13, border: "1px solid var(--line)", borderRadius: 6 }}
                data-testid="challan-qty-input"
              />
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Items Covered</label>
            <input
              type="text"
              value={form.items_covered}
              onChange={(e) => setForm({ ...form, items_covered: e.target.value })}
              style={{ width: "100%", height: 34, padding: "0 10px", fontSize: 13, border: "1px solid var(--line)", borderRadius: 6 }}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Transporter / Delivery Person</label>
              <input
                type="text"
                value={form.transporter}
                onChange={(e) => setForm({ ...form, transporter: e.target.value })}
                style={{ width: "100%", height: 34, padding: "0 10px", fontSize: 13, border: "1px solid var(--line)", borderRadius: 6 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Tracking / LR #</label>
              <input
                type="text"
                value={form.tracking_number}
                onChange={(e) => setForm({ ...form, tracking_number: e.target.value })}
                style={{ width: "100%", height: 34, padding: "0 10px", fontSize: 13, border: "1px solid var(--line)", borderRadius: 6 }}
              />
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>
              Document Attachment (PDF or Scan)
            </label>
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
              onChange={(e) => setFile(e.target.files[0])}
              style={{ fontSize: 12, width: "100%" }}
            />
            <small style={{ color: "#94a3b8", display: "block", marginTop: 2 }}>
              If no file is selected, the CRM will automatically generate a Challan PDF.
            </small>
          </div>

          {isEdit && (
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>
                Reason for change / replacement *
              </label>
              <input
                type="text"
                required
                placeholder="e.g. Corrected quantity or replaced signed document"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                style={{ width: "100%", height: 34, padding: "0 10px", fontSize: 13, border: "1px solid var(--line)", borderRadius: 6 }}
              />
            </div>
          )}

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Remarks</label>
            <textarea
              rows={2}
              value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })}
              style={{ width: "100%", padding: 8, fontSize: 12.5, border: "1px solid var(--line)", borderRadius: 6 }}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12 }}>
            <button type="button" className="outline-btn compact" onClick={onClose}>Cancel</button>
            <button
              type="submit"
              className="primary-btn compact"
              disabled={busy}
              style={{ minWidth: 120 }}
              data-testid="submit-challan-btn"
            >
              {busy ? "Saving…" : isEdit ? "Update Challan" : "Record Challan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Subcomponent: Mark Challan Received Modal (Stage C)
// -------------------------------------------------------------------------
function MarkReceiveModal({ challan, order, onClose, onSuccess }) {
  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().slice(0, 10));
  const [receivedBy, setReceivedBy] = useState("");
  const [remarks, setRemarks] = useState("");
  const [proofFile, setProofFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!receivedBy.trim()) {
      toast.error("Please enter the name of the recipient who confirmed receipt");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("received_date", receivedDate);
      fd.append("received_by", receivedBy.trim());
      if (remarks) fd.append("receipt_remarks", remarks);
      if (proofFile) fd.append("proof_file", proofFile);

      await api("patch", `/accounts/challans/${challan.challan_id}/receive`, fd);
      toast.success(`Challan #${challan.challan_number} marked as Received!`);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to mark Challan as received");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div className="panel" style={{ background: "#ffffff", borderRadius: 8, width: "100%", maxWidth: 480, padding: 22, boxShadow: "0 10px 25px rgba(0,0,0,0.1)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, borderBottom: "1px solid var(--line)", paddingBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "var(--navy)" }}>
            Mark Challan #{challan.challan_number} Received
          </h3>
          <button className="icon-btn" onClick={onClose}><X size={16} /></button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>
              Received Date *
            </label>
            <input
              type="date"
              required
              value={receivedDate}
              onChange={(e) => setReceivedDate(e.target.value)}
              style={{ width: "100%", height: 34, padding: "0 10px", fontSize: 13, border: "1px solid var(--line)", borderRadius: 6 }}
              data-testid="receive-date-input"
            />
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>
              Received By (Client Person / Receiver) *
            </label>
            <input
              type="text"
              required
              placeholder="e.g. John Doe / Warehouse Receiver"
              value={receivedBy}
              onChange={(e) => setReceivedBy(e.target.value)}
              style={{ width: "100%", height: 34, padding: "0 10px", fontSize: 13, border: "1px solid var(--line)", borderRadius: 6 }}
              data-testid="received-by-input"
            />
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>
              Signed Challan Proof / Photo (Optional)
            </label>
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={(e) => setProofFile(e.target.files[0])}
              style={{ fontSize: 12, width: "100%" }}
            />
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Receipt Remarks</label>
            <textarea
              rows={2}
              placeholder="e.g. Physical signed challan verified and filed"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              style={{ width: "100%", padding: 8, fontSize: 12.5, border: "1px solid var(--line)", borderRadius: 6 }}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12 }}>
            <button type="button" className="outline-btn compact" onClick={onClose}>Cancel</button>
            <button
              type="submit"
              className="primary-btn compact"
              disabled={busy}
              style={{ background: "#16a34a", minWidth: 130 }}
              data-testid="confirm-mark-received-btn"
            >
              {busy ? "Saving…" : "Confirm Receipt"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Subcomponent: Final Invoice Form Modal (Stage D)
// -------------------------------------------------------------------------
function InvoiceFormModal({ order, invoice, client, isEdit, onClose, onSuccess }) {
  const [form, setForm] = useState({
    invoice_number: invoice?.invoice_number || "",
    invoice_date: invoice?.invoice_date || new Date().toISOString().slice(0, 10),
    due_date: invoice?.due_date || "",
    payment_terms: invoice?.payment_terms || client?.payment_terms || "Net 30",
    taxable_amount: invoice?.taxable !== undefined ? invoice.taxable : order?.order_value || 0,
    cgst: invoice?.cgst !== undefined ? invoice.cgst : (order?.gst ? Math.round((order.gst / 2.0) * 100) / 100 : 0),
    sgst: invoice?.sgst !== undefined ? invoice.sgst : (order?.gst ? Math.round((order.gst / 2.0) * 100) / 100 : 0),
    igst: invoice?.igst || 0,
    total_amount: invoice?.total !== undefined ? invoice.total : order?.total_value || 0,
    remarks: invoice?.remarks || "",
    reason: "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const calculateTotal = (taxable, cgst, sgst, igst) => {
    const t = parseFloat(taxable) || 0;
    const c = parseFloat(cgst) || 0;
    const s = parseFloat(sgst) || 0;
    const i = parseFloat(igst) || 0;
    const tot = Math.round((t + c + s + i) * 100) / 100;
    setForm((f) => ({ ...f, taxable_amount: t, cgst: c, sgst: s, igst: i, total_amount: tot }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      if (!isEdit) {
        if (order.batch_id) fd.append("batch_id", order.batch_id);
        else fd.append("order_id", order.order_id);
        fd.append("client_id", client.client_id);
      }
      if (form.invoice_number.trim()) fd.append("invoice_number", form.invoice_number.trim());
      fd.append("invoice_date", form.invoice_date);
      if (form.due_date) fd.append("due_date", form.due_date);
      fd.append("payment_terms", form.payment_terms);
      fd.append("taxable_amount", form.taxable_amount);
      fd.append("cgst", form.cgst);
      fd.append("sgst", form.sgst);
      fd.append("igst", form.igst);
      fd.append("total_amount", form.total_amount);
      if (form.remarks) fd.append("remarks", form.remarks);
      if (isEdit && form.reason) fd.append("reason", form.reason);
      if (file) fd.append("file", file);

      if (isEdit) {
        await api("put", `/accounts/invoices/${invoice.invoice_id}`, fd);
        toast.success("Final Invoice updated successfully");
      } else {
        await api("post", `/accounts/invoices`, fd);
        toast.success("Final Invoice issued successfully");
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save Final Invoice");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div className="panel" style={{ background: "#ffffff", borderRadius: 8, width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto", padding: 22, boxShadow: "0 10px 25px rgba(0,0,0,0.1)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, borderBottom: "1px solid var(--line)", paddingBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "var(--navy)" }}>
            {isEdit ? "Edit / Replace Final Invoice" : "Generate Final Invoice"}
          </h3>
          <button className="icon-btn" onClick={onClose}><X size={16} /></button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>
              Invoice Number (Leave empty to auto-generate)
            </label>
            <input
              type="text"
              placeholder="e.g. INV-2610-001"
              value={form.invoice_number}
              onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
              style={{ width: "100%", height: 34, padding: "0 10px", fontSize: 13, border: "1px solid var(--line)", borderRadius: 6 }}
              data-testid="invoice-number-input"
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Invoice Date *</label>
              <input
                type="date"
                required
                value={form.invoice_date}
                onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
                style={{ width: "100%", height: 34, padding: "0 8px", fontSize: 13, border: "1px solid var(--line)", borderRadius: 6 }}
                data-testid="invoice-date-input"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Due Date</label>
              <input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                style={{ width: "100%", height: 34, padding: "0 8px", fontSize: 13, border: "1px solid var(--line)", borderRadius: 6 }}
              />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Taxable Value (₹) *</label>
              <input
                type="number"
                step="0.01"
                required
                value={form.taxable_amount}
                onChange={(e) => calculateTotal(e.target.value, form.cgst, form.sgst, form.igst)}
                style={{ width: "100%", height: 34, padding: "0 10px", fontSize: 13, border: "1px solid var(--line)", borderRadius: 6 }}
                data-testid="invoice-taxable-input"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Payment Terms</label>
              <input
                type="text"
                value={form.payment_terms}
                onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
                style={{ width: "100%", height: 34, padding: "0 10px", fontSize: 13, border: "1px solid var(--line)", borderRadius: 6 }}
              />
            </div>
          </div>

          {/* Tax Breakdown */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 3 }}>CGST (₹)</label>
              <input
                type="number"
                step="0.01"
                value={form.cgst}
                onChange={(e) => calculateTotal(form.taxable_amount, e.target.value, form.sgst, form.igst)}
                style={{ width: "100%", height: 32, padding: "0 8px", fontSize: 12, border: "1px solid var(--line)", borderRadius: 6 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 3 }}>SGST (₹)</label>
              <input
                type="number"
                step="0.01"
                value={form.sgst}
                onChange={(e) => calculateTotal(form.taxable_amount, form.cgst, e.target.value, form.igst)}
                style={{ width: "100%", height: 32, padding: "0 8px", fontSize: 12, border: "1px solid var(--line)", borderRadius: 6 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 3 }}>IGST (₹)</label>
              <input
                type="number"
                step="0.01"
                value={form.igst}
                onChange={(e) => calculateTotal(form.taxable_amount, form.cgst, form.sgst, e.target.value)}
                style={{ width: "100%", height: 32, padding: "0 8px", fontSize: 12, border: "1px solid var(--line)", borderRadius: 6 }}
              />
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Total Invoice Amount (₹) *</label>
            <input
              type="number"
              step="0.01"
              required
              value={form.total_amount}
              onChange={(e) => setForm({ ...form, total_amount: parseFloat(e.target.value) || 0 })}
              style={{ width: "100%", height: 34, padding: "0 10px", fontSize: 13, fontWeight: 700, border: "1px solid var(--line)", borderRadius: 6 }}
              data-testid="invoice-total-input"
            />
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>
              Document Attachment (PDF or Scan)
            </label>
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
              onChange={(e) => setFile(e.target.files[0])}
              style={{ fontSize: 12, width: "100%" }}
            />
            <small style={{ color: "#94a3b8", display: "block", marginTop: 2 }}>
              If no file is selected, the CRM will auto-generate an Invoice PDF.
            </small>
          </div>

          {isEdit && (
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>
                Reason for change / replacement *
              </label>
              <input
                type="text"
                required
                placeholder="e.g. Corrected GST breakdown or re-uploaded signed PDF"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                style={{ width: "100%", height: 34, padding: "0 10px", fontSize: 13, border: "1px solid var(--line)", borderRadius: 6 }}
              />
            </div>
          )}

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Remarks</label>
            <textarea
              rows={2}
              value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })}
              style={{ width: "100%", padding: 8, fontSize: 12.5, border: "1px solid var(--line)", borderRadius: 6 }}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12 }}>
            <button type="button" className="outline-btn compact" onClick={onClose}>Cancel</button>
            <button
              type="submit"
              className="primary-btn compact"
              disabled={busy}
              style={{ minWidth: 120 }}
              data-testid="submit-invoice-btn"
            >
              {busy ? "Saving…" : isEdit ? "Update Invoice" : "Issue Invoice"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Subcomponent: Audit Trail & Version History Modal
// -------------------------------------------------------------------------
function AuditTrailModal({ title, history, versions, onClose, onDownloadDoc }) {
  return (
    <div className="modal-backdrop" style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div className="panel" style={{ background: "#ffffff", borderRadius: 8, width: "100%", maxWidth: 540, maxHeight: "90vh", overflowY: "auto", padding: 22, boxShadow: "0 10px 25px rgba(0,0,0,0.1)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, borderBottom: "1px solid var(--line)", paddingBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "var(--navy)" }}>{title}</h3>
          <button className="icon-btn" onClick={onClose}><X size={16} /></button>
        </div>

        {/* Previous Document Versions */}
        {versions && versions.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <h4 style={{ margin: "0 0 8px", fontSize: 12.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
              Document Versions
            </h4>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {versions.map((ver, idx) => (
                <div key={idx} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, padding: "8px 10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 12, color: "var(--navy)" }}>Version {ver.version}: {ver.document_name}</span>
                    <div style={{ fontSize: 10.5, color: "#64748b" }}>Replaced on {ver.replaced_at?.slice(0, 10)} by {ver.replaced_by} · {ver.reason}</div>
                  </div>
                  {ver.document_id && (
                    <button
                      className="outline-btn compact"
                      style={{ fontSize: 11, height: 24, padding: "0 7px" }}
                      onClick={() => onDownloadDoc(ver.document_id)}
                    >
                      <Download size={11} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Audit Log Events */}
        <div>
          <h4 style={{ margin: "0 0 8px", fontSize: 12.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
            Audit Action Trail
          </h4>
          {(!history || history.length === 0) ? (
            <p style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>No audit records available.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {history.map((h, i) => (
                <div key={i} style={{ borderLeft: "2px solid #0284c7", paddingLeft: 10, fontSize: 12 }}>
                  <div style={{ fontWeight: 600, color: "var(--navy)" }}>{h.action}</div>
                  <div style={{ fontSize: 10.5, color: "#64748b" }}>
                    By <b>{h.by}</b> on {h.at?.replace("T", " ").slice(0, 19)}
                  </div>
                  {h.remarks && <div style={{ color: "#334155", marginTop: 2 }}>{h.remarks}</div>}
                  {h.reason && <div style={{ color: "#334155", marginTop: 2 }}>Reason: {h.reason}</div>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
          <button className="outline-btn compact" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Subcomponent: In-App Document Preview Modal
// -------------------------------------------------------------------------
function DocumentPreviewModal({ preview, onClose, onDownload }) {
  if (!preview || !preview.isOpen) return null;

  return (
    <div
      className="modal-backdrop"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.75)",
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      data-testid="document-preview-modal"
    >
      <div
        className="panel"
        style={{
          background: "#ffffff",
          borderRadius: 8,
          width: "100%",
          maxWidth: 960,
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 40px rgba(0,0,0,0.25)",
          overflow: "hidden",
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid var(--line)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "#f8fafc",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
            <FileText size={20} style={{ color: "#0284c7", flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: 15,
                    fontWeight: 700,
                    color: "var(--navy)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {preview.title}
                </h3>
                {preview.category && (
                  <span
                    className="badge"
                    style={{
                      fontSize: 10.5,
                      padding: "2px 7px",
                      background: "#e0f2fe",
                      color: "#0369a1",
                      border: "1px solid #bae6fd",
                      fontWeight: 600,
                    }}
                  >
                    {preview.category}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                Document Ref: <span className="mono" style={{ fontWeight: 600 }}>{preview.docId}</span> · {preview.docName}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {preview.url && (
              <>
                <button
                  type="button"
                  className="outline-btn compact"
                  style={{ fontSize: 11.5, height: 28, padding: "0 9px", display: "inline-flex", alignItems: "center", gap: 4 }}
                  onClick={() => window.open(preview.url, "_blank")}
                  title="Open in a separate browser tab"
                  data-testid="preview-popout-btn"
                >
                  <ExternalLink size={12} /> Open in Tab
                </button>
                <button
                  type="button"
                  className="outline-btn compact"
                  style={{ fontSize: 11.5, height: 28, padding: "0 9px", display: "inline-flex", alignItems: "center", gap: 4 }}
                  onClick={() => onDownload && onDownload(preview.docId, preview.docName)}
                  title="Download File"
                  data-testid="preview-download-btn"
                >
                  <Download size={12} /> Download
                </button>
              </>
            )}
            <button
              type="button"
              className="icon-btn"
              onClick={onClose}
              style={{ width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center" }}
              data-testid="close-preview-modal-btn"
              title="Close Preview"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Modal Body / Viewer */}
        <div style={{ flex: 1, position: "relative", background: "#f1f5f9", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {preview.loading && (
            <div style={{ textAlign: "center", color: "#64748b", padding: 30 }}>
              <RefreshCw size={26} className="spin" style={{ marginBottom: 12, color: "#0284c7" }} />
              <div style={{ fontSize: 13.5, fontWeight: 600 }}>Loading document preview…</div>
              <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 4 }}>Fetching authenticated file stream</div>
            </div>
          )}

          {preview.error && !preview.loading && (
            <div style={{ maxWidth: 500, margin: "0 auto", padding: 26, background: "#ffffff", borderRadius: 8, border: "1px solid #fecaca", textAlign: "center", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
              <AlertCircle size={40} style={{ color: "#ef4444", margin: "0 auto 12px" }} />
              <h4 style={{ margin: "0 0 8px", fontSize: 15.5, fontWeight: 700, color: "#991b1b" }}>Document Unavailable</h4>
              <p style={{ margin: "0 0 16px", fontSize: 13, color: "#475569", lineHeight: 1.5 }}>
                {preview.error}
              </p>
              <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
                <button type="button" className="outline-btn compact" onClick={onClose} style={{ padding: "0 16px" }}>
                  Close
                </button>
              </div>
            </div>
          )}

          {preview.url && !preview.loading && (
            preview.contentType?.startsWith("image/") ? (
              <div style={{ width: "100%", height: "100%", overflow: "auto", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
                <img
                  src={preview.url}
                  alt={preview.title}
                  style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 4, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                />
              </div>
            ) : preview.contentType === "application/pdf" || preview.docName?.toLowerCase().endsWith(".pdf") ? (
              <iframe
                src={preview.url}
                title={preview.title}
                style={{ width: "100%", height: "100%", border: "none" }}
              />
            ) : (
              <div style={{ textAlign: "center", background: "#ffffff", padding: 36, borderRadius: 8, border: "1px solid #e2e8f0", maxWidth: 440 }}>
                <FileText size={44} style={{ color: "#0284c7", margin: "0 auto 12px" }} />
                <h4 style={{ margin: "0 0 6px", fontSize: 15, color: "var(--navy)" }}>{preview.docName}</h4>
                <p style={{ margin: "0 0 16px", fontSize: 12.5, color: "#64748b" }}>
                  Format: <b>{preview.contentType || "binary"}</b>.<br />
                  This document type can be downloaded and opened with your system viewer.
                </p>
                <button
                  type="button"
                  className="primary-btn compact"
                  onClick={() => onDownload && onDownload(preview.docId, preview.docName)}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                >
                  <Download size={14} /> Download File
                </button>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

